/**
 * Purchase orders panel (Phase 3b) — the persisted reorder POs, shown under the draft. An OPEN PO
 * (QUEUED / ORDERED) nets its units out of the reorder signal so the same shirts stop showing short.
 *
 * QUEUED POs are the reminder to actually place the order in S&S (paste the FSP PO#), then Mark ordered.
 * Cancel voids a mistaken PO and un-nets its SKUs. Cost/actions are admin/inventory-admin only — the
 * server gates both; this panel is only rendered for writers (cosmetic).
 *
 * In-app live ordering: a manager (admin group) can place the PO on S&S directly with one confirm
 * ("Place order"). It is a REAL, billable order — the server gates it on the `admin` group, an explicit
 * confirm, a QUEUED PO with no unresolved prior attempt, and a pre-POST idempotency marker. The manual
 * paste-the-FSP-number workflow stays available for everyone (and for non-managers).
 *
 * UI refresh PR-6b (2026-08, docs/mockups/purchase-orders-v2): the 640px table with inline-expanding
 * line items became a LIST — one row per PO, one entry point, one primary action — plus a detail
 * sheet holding the lines, the S&S reference and the rarer actions. The old shape nested a table
 * inside a table and pushed every other PO off the screen; it also showed all history forever, so the
 * single row you can act on was the hardest to find. Open POs are the list now; closed ones collapse.
 *
 * Copying the FSP number is GONE, not moved: the owner places every FSP purchase order through this
 * app (2026-08-08), so the number is never pasted into S&S's PO field. Orders placed on
 * ssactivewear.com are for stock this app doesn't track, and never become an FSP PO.
 *
 * Both confirms — the billable place-order and the void — are modal dialogs. A real-money
 * confirm rendered inside a 640px horizontally-scrolling table cell is unreadable on a phone, and the
 * shipping-method select + cost line had to be crammed next to the buttons. Two consequences the
 * dialogs handle explicitly:
 *   1. Only ONE confirm can be open at a time — now structural (`confirming` is a single tagged
 *      value), not an incidental pair of cross-guards.
 *   2. A lifted confirm no longer dies with its row, so each dialog RE-DERIVES its PO from the live
 *      list every render and refuses (loudly, without sending) when the PO changed underneath.
 */

import {
  DEFAULT_SHIPPING_METHOD,
  type ShippingMethodCode,
  SS_SHIPPING_METHODS,
} from '@fsp/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  ChevronRight,
  CreditCard,
  MapPin,
  PackageCheck,
  Printer,
  ShoppingCart,
} from 'lucide-react';
import { type ReactNode, useRef, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useApiClient } from '@/lib/api/hooks';
import { useAuth } from '@/lib/auth-state';
import { cn } from '@/lib/utils';
import { blankLabel, invalidateInventory, variantLabel } from './inventory-lib';
import { PaymentProfileDialog } from './payment-profile-dialog';
import { money } from './purchase-draft-lib';
import {
  hasUnresolvedAttempt,
  isCancelable,
  isOpenPo,
  isPlaceable,
  type PurchaseOrderSummary,
  poStatusMeta,
} from './purchase-order-lib';
import { PurchaseOrderPrint } from './purchase-order-print';
import { ShipToConfigDialog } from './shipto-config-dialog';

const TONE_CLASS: Record<ReturnType<typeof poStatusMeta>['tone'], string> = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  active: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
  done: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  void: 'bg-muted text-muted-foreground line-through',
};

/* Detail-sheet line grid: blank | qty | [received] | unit | line total (collapses on a phone). The
   Received column only exists once something HAS been received — carrying it always left an empty
   5rem hole between Qty and Unit. */
const LINE_GRID = 'sm:grid sm:items-center sm:gap-x-2 sm:grid-cols-[minmax(0,1fr)_4rem_5rem_6rem]';
const LINE_GRID_RECEIVED =
  'sm:grid sm:items-center sm:gap-x-2 sm:grid-cols-[minmax(0,1fr)_4rem_5rem_5rem_6rem]';

const shortDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
};

/**
 * Client mirror of the server's `SS_INAPP_ORDERING_ENABLED` kill-switch — the Place-order affordance is
 * hidden when off. ON: managers can place directly. This is a fast client-side hide; the server is the
 * real gate (403 `ordering_disabled` when its env switch is off, plus the manager `admin` gate).
 */
const IN_APP_SS_ORDERING_ENABLED = true;

/** A place-order failure that carries the server's error `type` so the UI can distinguish a definite
 * rejection (fixable, resend allowed) from an UNCONFIRMED outcome (an order may exist — do NOT resend). */
class PlaceOrderError extends Error {
  readonly kind: 'unknown' | 'rejected' | 'other';
  constructor(message: string, kind: 'unknown' | 'rejected' | 'other') {
    super(message);
    this.kind = kind;
  }
}

/** Per-order S&S shipping-method picker, shown inside the place-order confirm flow. The option list is the
 * single source of truth in @fsp/contracts (SS_SHIPPING_METHODS); the default is `1` = Ground. */
function ShipMethodSelect({
  value,
  onChange,
  disabled,
}: {
  value: ShippingMethodCode;
  onChange: (v: ShippingMethodCode) => void;
  disabled?: boolean;
}) {
  return (
    <Select
      value={value}
      onValueChange={(v) => onChange(v as ShippingMethodCode)}
      disabled={disabled}
    >
      <SelectTrigger className="h-8 w-[188px] text-xs" aria-label="Shipping method">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {SS_SHIPPING_METHODS.map((m) => (
          <SelectItem key={m.code} value={m.code} className="text-xs">
            {m.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function PurchaseOrdersPanel() {
  const client = useApiClient();
  const qc = useQueryClient();
  const { state } = useAuth();
  // Placing a live, billable order is manager-only (the `admin` group) — the server enforces it; this
  // hides the affordance for non-managers. Other PO actions stay open to any inventory writer.
  const isManager = state.status === 'signed-in' && state.role.isAdmin;
  // The PO whose detail sheet is open (by number — the record itself is re-derived from the live list).
  const [detailOf, setDetailOf] = useState<string | null>(null);
  const [printing, setPrinting] = useState<string | null>(null);
  const [shipToOpen, setShipToOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  // The single open confirm — voiding a PO, or the LIVE (billable) place-order. One tagged value
  // instead of two independent strings: "only one confirm at a time" is then unrepresentable-otherwise
  // rather than upheld by cross-guards in the row.
  const [confirming, setConfirming] = useState<{
    kind: 'cancel' | 'place';
    poNumber: string;
  } | null>(null);
  // The S&S shipping method for the PO currently being placed (one confirm flow is open at a time).
  const [shipMethod, setShipMethod] = useState<ShippingMethodCode>(DEFAULT_SHIPPING_METHOD);
  // Single-flight latch for the BILLABLE POST. `disabled={busy}` alone loses a double-tap race — the
  // second click can land before React re-renders with isPending, and useMutation does not dedupe, so
  // two orders go out. A ref flips synchronously inside the handler, before any render.
  const placeInFlight = useRef(false);

  const query = useQuery({
    queryKey: ['purchase-orders'],
    queryFn: async () => {
      const { data, error } = await client.GET('/purchase-orders');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load purchase orders.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  // Ship-to must be set before a live order can be placed (the server 422s otherwise; this drives the UI).
  const shipToQuery = useQuery({
    queryKey: ['shipto-config'],
    queryFn: async () => {
      const { data, error } = await client.GET('/shipto-config/current');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the ship-to address.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });
  const shipToSet = shipToQuery.data?.body != null;

  // Place a PO to S&S as a LIVE, BILLABLE order. REAL money — gated by the manager (`admin`) role plus a
  // per-PO confirm (this dialog shows pieces + est. cost). On any unconfirmed outcome the server holds the
  // PO and returns `order_outcome_unknown`; the UI surfaces that distinctly (do NOT resend).
  const placeOrder = useMutation({
    mutationFn: async ({
      poNumber,
      shippingMethod,
    }: {
      poNumber: string;
      shippingMethod: ShippingMethodCode;
    }) => {
      // A fetch-level rejection (network / CORS / DNS) on a billable POST is an UNCONFIRMED outcome — the
      // order MAY already have been placed. Classify it as `unknown` (never a silent throw) so the UI shows
      // the loud do-NOT-resend / check-the-portal alert rather than leaving the manager with no feedback.
      const { data, error } = await client
        .POST('/purchase-orders/{poNumber}/place-order', {
          params: { path: { poNumber } },
          body: { confirm: true, shippingMethod },
        })
        .catch((e: unknown) => {
          throw new PlaceOrderError(
            e instanceof Error ? e.message : 'Network error placing the order.',
            'unknown',
          );
        });
      if (error) {
        const type = error?.error?.type;
        const kind =
          type === 'order_outcome_unknown'
            ? 'unknown'
            : type === 'upstream_error'
              ? 'rejected'
              : 'other';
        throw new PlaceOrderError(error?.error?.detail ?? 'Failed to place the order.', kind);
      }
      if (!data?.ok) throw new PlaceOrderError('Empty response from the server.', 'other');
      return data.data;
    },
    // Runs on BOTH success and failure: always close the confirm dialog and refetch the PO list so the row
    // reflects the server's TRUE state — an unresolved attempt then hides the button + shows the reconcile
    // badge, and a lost-response success shows as ORDERED. Without this, a failed attempt would leave a
    // stale, enabled "Place order" button contradicting the "blocked from resending" alert.
    onSettled: () => {
      placeInFlight.current = false;
      setConfirming(null);
      invalidateInventory(qc);
    },
  });

  const markOrdered = useMutation({
    mutationFn: async (poNumber: string) => {
      const { data, error } = await client.POST('/purchase-orders/{poNumber}/mark-ordered', {
        params: { path: { poNumber } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to mark ordered.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
    onSuccess: () => {
      setDetailOf(null);
      invalidateInventory(qc);
    },
  });

  const markReceived = useMutation({
    mutationFn: async (poNumber: string) => {
      const { data, error } = await client.POST('/purchase-orders/{poNumber}/close', {
        params: { path: { poNumber } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to mark received.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
    onSuccess: () => {
      setDetailOf(null);
      invalidateInventory(qc);
    },
  });

  const cancel = useMutation({
    mutationFn: async (poNumber: string) => {
      const { data, error } = await client.POST('/purchase-orders/{poNumber}/cancel', {
        params: { path: { poNumber } },
        body: {},
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to cancel.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
    onSuccess: () => {
      setConfirming(null);
      invalidateInventory(qc);
    },
  });

  const orders = query.data?.items ?? [];
  // Open (actionable) first, then the rest — each newest-first.
  const sorted = [...orders].sort((a, b) => {
    const openDelta = Number(isOpenPo(b.status)) - Number(isOpenPo(a.status));
    if (openDelta !== 0) return openDelta;
    return b.createdAt.localeCompare(a.createdAt);
  });
  // Open POs are the working list; closed ones (received / canceled) collapse into history — with 1
  // open and 8 closed in prod, the row you can act on was the hardest to find.
  const openPos = sorted.filter((o) => isOpenPo(o.status));
  const closedPos = sorted.filter((o) => !isOpenPo(o.status));
  const openCount = openPos.length;
  const detailPo = detailOf == null ? undefined : orders.find((o) => o.poNumber === detailOf);
  const busy =
    markOrdered.isPending || markReceived.isPending || cancel.isPending || placeOrder.isPending;
  const actionError = markOrdered.error ?? markReceived.error ?? cancel.error;

  // Both confirms read their PO from the LIVE list every render — `undefined` means it was cancelled,
  // received, or otherwise refetched away while the dialog stood open.
  const confirmingPo =
    confirming == null ? undefined : orders.find((o) => o.poNumber === confirming.poNumber);

  // Send the REAL, billable order. The synchronous latch stops a double-tap that outran `disabled`,
  // and the live-PO re-check runs at CLICK time as well as at render time — the dialog already hides
  // the button when the PO went stale, so this is the belt to that suspenders.
  const confirmPlace = () => {
    if (placeInFlight.current) return;
    if (confirming?.kind !== 'place') return;
    if (confirmingPo == null || !isPlaceable(confirmingPo)) return;
    placeInFlight.current = true;
    placeOrder.mutate({ poNumber: confirming.poNumber, shippingMethod: shipMethod });
  };

  // No latch here on purpose: a replayed cancel is idempotent server-side (an already-CANCELED PO
  // returns its current view, 200), so a double-tap costs nothing and can't double-void.
  const confirmCancel = () => {
    if (confirming?.kind !== 'cancel') return;
    if (confirmingPo == null || !isCancelable(confirmingPo)) return;
    cancel.mutate(confirming.poNumber);
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold">
          Purchase orders
          {openCount > 0 && (
            <span className="ml-2 text-xs font-normal text-muted-foreground">{openCount} open</span>
          )}
        </h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShipToOpen(true)}>
            <MapPin className="size-4" />
            Ship-to address
          </Button>
          <Button variant="outline" size="sm" onClick={() => setPaymentOpen(true)}>
            <CreditCard className="size-4" />
            Payment method
          </Button>
        </div>
      </div>

      <ShipToConfigDialog open={shipToOpen} onClose={() => setShipToOpen(false)} />
      <PaymentProfileDialog open={paymentOpen} onClose={() => setPaymentOpen(false)} />

      {/* LIVE place-order — a real, billable order was CONFIRMED by S&S. */}
      {placeOrder.isSuccess && placeOrder.data && (
        <Alert>
          <ShoppingCart className="size-4 text-emerald-600 dark:text-emerald-400" />
          <AlertTitle>
            REAL order placed for {placeOrder.data.poNumber} — this is a billable S&amp;S order
          </AlertTitle>
          <AlertDescription>
            S&amp;S order number{placeOrder.data.orderNumbers.length === 1 ? '' : 's'}:{' '}
            <span className="font-mono">{placeOrder.data.orderNumbers.join(', ')}</span>. The PO is
            now <span className="font-medium">{placeOrder.data.status}</span>. S&amp;S will fulfill
            and bill this order — cancel it in the S&amp;S portal if it was a mistake.
            {placeOrder.data.lineErrors.length > 0 && (
              <span className="mt-1 block text-destructive">
                Line issues: {placeOrder.data.lineErrors.map((e) => e.message).join('; ')}
              </span>
            )}
            {placeOrder.data.auditWarning && (
              <span className="mt-1 block font-medium text-amber-700 dark:text-amber-400">
                {placeOrder.data.auditWarning}
              </span>
            )}
          </AlertDescription>
        </Alert>
      )}
      {placeOrder.error instanceof PlaceOrderError &&
        (placeOrder.error.kind === 'unknown' ? (
          // The most dangerous case: the order MAY exist. Loud, and explicitly do-NOT-resend.
          <Alert variant="destructive">
            <AlertTriangle className="size-4" />
            <AlertTitle>Order outcome could not be confirmed — do NOT resend</AlertTitle>
            <AlertDescription>
              {placeOrder.error.message}
              <span className="mt-1 block font-medium">
                Open the S&amp;S portal and check whether an order was created for this PO before
                doing anything else. The PO is now blocked from resending until the attempt is
                reconciled.
              </span>
            </AlertDescription>
          </Alert>
        ) : (
          <Alert variant="destructive">
            <AlertTitle>Order was not placed</AlertTitle>
            <AlertDescription>{placeOrder.error.message}</AlertDescription>
          </Alert>
        ))}

      {actionError instanceof Error && (
        <Alert variant="destructive">
          <AlertTitle>Action failed</AlertTitle>
          <AlertDescription>{actionError.message}</AlertDescription>
        </Alert>
      )}
      {query.isError && (
        <Alert variant="destructive">
          <AlertTitle>Couldn't load purchase orders</AlertTitle>
          <AlertDescription>
            {query.error instanceof Error ? query.error.message : 'Unknown error.'}
          </AlertDescription>
        </Alert>
      )}

      {query.isLoading && <Skeleton className="h-24 w-full" />}

      {query.data && orders.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-1.5 py-8 text-center">
            <PackageCheck className="size-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              No purchase orders yet. Create one from the draft above once you've reviewed the
              quantities.
            </p>
          </CardContent>
        </Card>
      )}

      {openPos.length > 0 && (
        <ul className="overflow-hidden rounded-lg border">
          {openPos.map((po) => (
            <PoRow
              key={po.poNumber}
              po={po}
              onOpen={() => setDetailOf(po.poNumber)}
              onMarkOrdered={() => markOrdered.mutate(po.poNumber)}
              onMarkReceived={() => markReceived.mutate(po.poNumber)}
              busy={busy}
            />
          ))}
        </ul>
      )}

      {orders.length > 0 && openPos.length === 0 && (
        <p className="rounded-lg border border-dashed px-3 py-4 text-center text-sm text-muted-foreground">
          Nothing open — every purchase order has been received or canceled.
        </p>
      )}

      {closedPos.length > 0 && (
        <details className="group">
          <summary className="cursor-pointer list-none py-2 text-sm text-muted-foreground hover:text-foreground">
            <ChevronRight
              className="mr-1.5 inline size-3.5 transition-transform group-open:rotate-90"
              aria-hidden
            />
            {closedPos.length} closed purchase order{closedPos.length === 1 ? '' : 's'}
          </summary>
          <ul className="overflow-hidden rounded-lg border">
            {closedPos.map((po) => (
              <ClosedPoRow key={po.poNumber} po={po} onOpen={() => setDetailOf(po.poNumber)} />
            ))}
          </ul>
        </details>
      )}

      <PoDetailSheet
        po={detailPo}
        poNumber={detailOf}
        onClose={() => setDetailOf(null)}
        onPrint={() => {
          // Close the sheet first. The print view is a body-level portal at z-50, and an open Radix
          // modal sets `pointer-events: none` on the body outside its own layer — leaving Print and
          // Close visible but dead. Same reason the confirms close it.
          setPrinting(detailOf);
          setDetailOf(null);
        }}
        onMarkOrdered={() => detailOf && markOrdered.mutate(detailOf)}
        onMarkReceived={() => detailOf && markReceived.mutate(detailOf)}
        onRequestPlace={() => {
          setShipMethod(DEFAULT_SHIPPING_METHOD);
          if (detailOf) setConfirming({ kind: 'place', poNumber: detailOf });
          setDetailOf(null);
        }}
        onRequestCancel={() => {
          if (detailOf) setConfirming({ kind: 'cancel', poNumber: detailOf });
          setDetailOf(null);
        }}
        isManager={isManager}
        shipToSet={shipToSet}
        busy={busy}
      />

      {confirming?.kind === 'place' && (
        <PlaceOrderDialog
          po={confirmingPo}
          poNumber={confirming.poNumber}
          shipMethod={shipMethod}
          onShipMethodChange={setShipMethod}
          onConfirm={confirmPlace}
          onClose={() => setConfirming(null)}
          pending={placeOrder.isPending}
        />
      )}
      {confirming?.kind === 'cancel' && (
        <CancelPoDialog
          po={confirmingPo}
          poNumber={confirming.poNumber}
          onConfirm={confirmCancel}
          onClose={() => setConfirming(null)}
          pending={cancel.isPending}
          error={cancel.error}
        />
      )}

      {printing && <PurchaseOrderPrint poNumber={printing} onClose={() => setPrinting(null)} />}
    </section>
  );
}

/**
 * The billable confirm. Shows exactly what will be spent (PO, pieces, estimate) plus the shipping
 * method, and never renders a send button for a PO that is no longer placeable.
 */
function PlaceOrderDialog({
  po,
  poNumber,
  shipMethod,
  onShipMethodChange,
  onConfirm,
  onClose,
  pending,
}: {
  po: PurchaseOrderSummary | undefined;
  poNumber: string;
  shipMethod: ShippingMethodCode;
  onShipMethodChange: (v: ShippingMethodCode) => void;
  onConfirm: () => void;
  onClose: () => void;
  pending: boolean;
}) {
  // Gone from the list, or no longer QUEUED-with-a-clean-attempt. While the POST is in flight we keep
  // showing the confirm body: the send already happened, and onSettled closes this dialog anyway.
  const changed = !pending && (po == null || !isPlaceable(po));
  return (
    <ConfirmDialog pending={pending} onClose={onClose}>
      <DialogHeader>
        <DialogTitle>{changed ? 'This PO changed' : `Place ${poNumber} with S&S?`}</DialogTitle>
        <DialogDescription>
          {changed
            ? 'Nothing was sent. It is no longer a queued order with a clean attempt history — close this and check the list.'
            : `${po?.totalUnits ?? 0} pcs · about ${money(po?.estimatedTotal ?? null)} — a REAL, billable S&S order, not a test.`}
        </DialogDescription>
      </DialogHeader>
      {!changed && (
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-muted-foreground">Shipping</span>
          <ShipMethodSelect value={shipMethod} onChange={onShipMethodChange} disabled={pending} />
        </div>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={pending}>
          {changed ? 'Close' : 'Keep it queued'}
        </Button>
        {!changed && (
          <Button variant="destructive" onClick={onConfirm} disabled={pending}>
            <ShoppingCart className="size-4" />
            {pending ? 'Placing…' : 'Place real order'}
          </Button>
        )}
      </DialogFooter>
    </ConfirmDialog>
  );
}

/** Voiding a PO — FSP-side only, which the copy has to say out loud for an already-ORDERED PO. */
function CancelPoDialog({
  po,
  poNumber,
  onConfirm,
  onClose,
  pending,
  error,
}: {
  po: PurchaseOrderSummary | undefined;
  poNumber: string;
  onConfirm: () => void;
  onClose: () => void;
  pending: boolean;
  /** A failed cancel leaves this dialog OPEN (unlike place-order, which closes on settle), so the
   * reason has to render in here — the panel-level alert sits behind the modal overlay. */
  error: Error | null;
}) {
  const changed = !pending && (po == null || !isCancelable(po));
  const wasOrdered = po?.status === 'ORDERED';
  return (
    <ConfirmDialog pending={pending} onClose={onClose}>
      <DialogHeader>
        <DialogTitle>{changed ? 'This PO changed' : `Cancel ${poNumber}?`}</DialogTitle>
        <DialogDescription>
          {changed
            ? 'Nothing was cancelled. It is no longer an open purchase order — close this and check the list.'
            : 'Its blanks go back to showing short, so they turn up in the next draft.'}
        </DialogDescription>
      </DialogHeader>
      {!changed && wasOrdered && (
        <Alert variant="destructive">
          <AlertTriangle className="size-4" />
          <AlertTitle>This does not cancel anything at S&amp;S</AlertTitle>
          <AlertDescription>
            It only voids the FSP record. If the order is already with S&amp;S, cancel it in the
            S&amp;S portal too.
          </AlertDescription>
        </Alert>
      )}
      {error && (
        <Alert variant="destructive">
          <AlertTitle>Nothing was cancelled</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={pending}>
          {changed ? 'Close' : 'Keep it'}
        </Button>
        {!changed && (
          <Button variant="destructive" onClick={onConfirm} disabled={pending}>
            {pending ? 'Cancelling…' : 'Cancel this PO'}
          </Button>
        )}
      </DialogFooter>
    </ConfirmDialog>
  );
}

/**
 * Shared modal shell for both confirms. Dismissal (Esc / overlay / the X) is BLOCKED while the write
 * is in flight — otherwise a mid-flight dismiss drops the operator back onto a trigger that is about
 * to re-enable, with no idea whether the first attempt landed. Each mutation's `onSettled`/`onSuccess`
 * closes it, and the outcome alerts live at panel level so they survive the close either way.
 */
export function ConfirmDialog({
  pending,
  onClose,
  children,
}: {
  pending: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !pending) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md" showCloseButton={!pending}>
        {children}
      </DialogContent>
    </Dialog>
  );
}

/* ---------------------------------------------------------------------------------------------
 * The list
 * ------------------------------------------------------------------------------------------- */

/**
 * The PO's lifecycle in one line. It used to say only "Created …", so an ORDERED PO sitting right
 * above its S&S order number never said WHEN it went to S&S — and a received one never said when it
 * landed. All three dates are on the summary already.
 */
function poDateLine(po: PurchaseOrderSummary): string {
  const bits = [`Created ${shortDate(po.createdAt)}`];
  if (po.orderedAt) bits.push(`ordered ${shortDate(po.orderedAt)}`);
  if (po.closedAt) bits.push(`received ${shortDate(po.closedAt)}`);
  if (po.canceledAt) bits.push(`canceled ${shortDate(po.canceledAt)}`);
  return bits.join(' · ');
}

/** Everything a PO row says besides its number: size, money, when, and the S&S reference. */
function poMetaLine(po: PurchaseOrderSummary): string {
  const bits = [
    `${po.totalUnits} piece${po.totalUnits === 1 ? '' : 's'}`,
    `${money(po.estimatedTotal)} est.`,
    shortDate(po.createdAt),
  ];
  if (po.ssOrderNumbers && po.ssOrderNumbers.length > 0) {
    bits.push(`S&S #${po.ssOrderNumbers.join(', ')}`);
  }
  return bits.join(' · ');
}

/**
 * One OPEN purchase order. The whole row opens the detail sheet; beside it sits the single action
 * you would take next, and nothing else — Print, Cancel and the billable Place order live in the
 * sheet, which puts real money one deliberate tap deeper than a mis-tap can reach.
 */
function PoRow({
  po,
  onOpen,
  onMarkOrdered,
  onMarkReceived,
  busy,
}: {
  po: PurchaseOrderSummary;
  onOpen: () => void;
  onMarkOrdered: () => void;
  onMarkReceived: () => void;
  busy: boolean;
}) {
  const meta = poStatusMeta(po.status);
  const attemptUnresolved = hasUnresolvedAttempt(po);
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b px-3 py-2.5 last:border-b-0 odd:bg-muted/20">
      <button
        type="button"
        onClick={onOpen}
        className="min-w-0 flex-1 text-left"
        title={`Open ${po.poNumber}`}
      >
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-semibold">{po.poNumber}</span>
          <span
            className={cn(
              'inline-block rounded-full px-2 py-0.5 text-xs font-medium',
              TONE_CLASS[meta.tone],
            )}
          >
            {meta.label}
          </span>
          {po.source === 'manual' && (
            <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              Manual
            </span>
          )}
        </span>
        <span className="mt-0.5 block truncate text-xs text-muted-foreground">
          {poMetaLine(po)}
        </span>
        {po.note && <span className="block truncate text-xs text-muted-foreground">{po.note}</span>}
        {attemptUnresolved && (
          <span className="mt-0.5 flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-400">
            <AlertTriangle className="size-3 shrink-0" aria-hidden />
            Order attempt unresolved — check the S&amp;S portal
          </span>
        )}
      </button>
      {po.status === 'QUEUED' && (
        <Button variant="outline" size="sm" onClick={onMarkOrdered} disabled={busy}>
          <CheckCircle2 className="size-4" />
          Mark ordered
        </Button>
      )}
      {po.status === 'ORDERED' && (
        <Button variant="outline" size="sm" onClick={onMarkReceived} disabled={busy}>
          <PackageCheck className="size-4" />
          Mark received
        </Button>
      )}
    </li>
  );
}

/** A finished PO — received or voided. Nothing to do but look at it. */
function ClosedPoRow({ po, onOpen }: { po: PurchaseOrderSummary; onOpen: () => void }) {
  const meta = poStatusMeta(po.status);
  return (
    <li className="border-b last:border-b-0 odd:bg-muted/20">
      <button
        type="button"
        onClick={onOpen}
        title={`Open ${po.poNumber}`}
        className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-left text-sm hover:bg-muted/40"
      >
        <span className={cn('font-medium', po.status === 'CANCELED' && 'text-muted-foreground')}>
          {po.poNumber}
        </span>
        <span
          className={cn(
            'inline-block rounded-full px-2 py-0.5 text-xs font-medium',
            TONE_CLASS[meta.tone],
          )}
        >
          {meta.label}
        </span>
        <span className="ml-auto truncate text-xs text-muted-foreground">{poMetaLine(po)}</span>
      </button>
    </li>
  );
}

/* ---------------------------------------------------------------------------------------------
 * The detail sheet
 * ------------------------------------------------------------------------------------------- */

/**
 * One purchase order in full: totals, the S&S reference, every line, and every action. This replaces
 * the old inline expansion, which rendered a table INSIDE the list table and shoved the remaining POs
 * off the screen.
 *
 * Like the confirms, it re-derives its PO from the live list every render — `po` going undefined
 * means it was refetched away underneath, and the sheet says so instead of offering actions on a
 * record that no longer exists.
 */
function PoDetailSheet({
  po,
  poNumber,
  onClose,
  onPrint,
  onMarkOrdered,
  onMarkReceived,
  onRequestPlace,
  onRequestCancel,
  isManager,
  shipToSet,
  busy,
}: {
  po: PurchaseOrderSummary | undefined;
  poNumber: string | null;
  onClose: () => void;
  onPrint: () => void;
  onMarkOrdered: () => void;
  onMarkReceived: () => void;
  onRequestPlace: () => void;
  onRequestCancel: () => void;
  isManager: boolean;
  shipToSet: boolean;
  busy: boolean;
}) {
  const meta = po ? poStatusMeta(po.status) : null;
  return (
    <Dialog open={poNumber != null} onOpenChange={(next) => !next && onClose()}>
      {/* Flex column, NOT a scrolling box. `overflow-y-auto` on DialogContent scrolled the header away
          with it — and the close X is `absolute top-4 right-4` INSIDE that box, so on a long PO the only
          way out was the sliver of backdrop left by max-h-85vh (no Esc key on a phone). Only the middle
          scrolls now, so identity and actions are always on screen. `min-h-0` is what lets the middle
          child actually scroll inside a flex column. */}
      <DialogContent className="max-h-[85vh] sm:max-w-2xl" scrollBody={false}>
        <DialogHeader className="shrink-0 text-left">
          <DialogTitle className="flex flex-wrap items-center gap-2">
            {poNumber}
            {meta && (
              <span
                className={cn(
                  'inline-block rounded-full px-2 py-0.5 text-xs font-medium',
                  TONE_CLASS[meta.tone],
                )}
              >
                {meta.label}
              </span>
            )}
            {po?.source === 'manual' && (
              <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                Manual
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {po ? poDateLine(po) : 'This purchase order is no longer listed.'}
          </DialogDescription>
        </DialogHeader>

        {po && (
          <>
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
              <div className="flex flex-wrap gap-6 rounded-lg bg-muted/50 px-4 py-3">
                <Total label="Pieces" value={String(po.totalUnits)} />
                <Total label="Est. total" value={money(po.estimatedTotal)} />
                <Total label="Lines" value={String(po.lineCount)} />
              </div>

              {po.ssOrderNumbers && po.ssOrderNumbers.length > 0 && (
                <p className="text-sm text-muted-foreground">
                  S&amp;S order{' '}
                  <span className="font-mono text-foreground">{po.ssOrderNumbers.join(', ')}</span>
                </p>
              )}
              {po.note && <p className="text-sm text-muted-foreground">{po.note}</p>}
              {hasUnresolvedAttempt(po) && (
                <Alert variant="destructive">
                  <AlertTriangle className="size-4" />
                  <AlertTitle>Order attempt unresolved</AlertTitle>
                  <AlertDescription>
                    Check the S&amp;S portal for an order against this PO before doing anything
                    else.
                  </AlertDescription>
                </Alert>
              )}

              <PoLines poNumber={po.poNumber} />
            </div>

            {/* F6: the destructive pair gets its own row on a phone (basis-full) instead of being
                orphaned there by a bare spacer. Deliberate separation, not an accident of wrapping. */}
            <div className="flex shrink-0 flex-wrap items-center gap-2 border-t pt-4">
              <Button variant="outline" size="sm" onClick={onPrint}>
                <Printer className="size-4" />
                Print
              </Button>
              {po.status === 'QUEUED' && (
                <Button variant="outline" size="sm" onClick={onMarkOrdered} disabled={busy}>
                  <CheckCircle2 className="size-4" />
                  Mark ordered
                </Button>
              )}
              {po.status === 'ORDERED' && (
                <Button variant="outline" size="sm" onClick={onMarkReceived} disabled={busy}>
                  <PackageCheck className="size-4" />
                  Mark received
                </Button>
              )}
              <span className="hidden flex-1 sm:block" />
              <span className="flex basis-full gap-2 sm:basis-auto">
                {IN_APP_SS_ORDERING_ENABLED && isManager && isPlaceable(po) && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={onRequestPlace}
                    disabled={busy || !shipToSet}
                    title={
                      shipToSet
                        ? 'Place this PO to S&S as a REAL, billable order (not a test)'
                        : 'Set a ship-to address first (Ship-to address button above)'
                    }
                  >
                    <ShoppingCart className="size-4" />
                    Place order…
                  </Button>
                )}
                {isCancelable(po) && (
                  <Button variant="ghost" size="sm" onClick={onRequestCancel} disabled={busy}>
                    <Ban className="size-4" />
                    Cancel PO…
                  </Button>
                )}
              </span>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Total({ label, value }: { label: string; value: string }) {
  return (
    <span>
      <span className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="block text-lg font-semibold tabular-nums">{value}</span>
    </span>
  );
}

/** The line items of one PO (shared cache key with the print doc + list invalidation). */
function PoLines({ poNumber }: { poNumber: string }) {
  const client = useApiClient();
  const query = useQuery({
    queryKey: ['purchase-orders', poNumber],
    queryFn: async () => {
      const { data, error } = await client.GET('/purchase-orders/{poNumber}', {
        params: { path: { poNumber } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the purchase order.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  if (query.isPending) return <Skeleton className="h-24 w-full" />;
  if (query.isError) {
    return (
      <p className="text-sm text-destructive">
        {query.error instanceof Error ? query.error.message : 'Could not load the line items.'}
      </p>
    );
  }
  const po = query.data;
  const showReceived = po.lines.some((l) => l.receivedQty > 0);
  const grid = showReceived ? LINE_GRID_RECEIVED : LINE_GRID;

  return (
    <div className="overflow-hidden rounded-lg border">
      <div
        className={cn(
          grid,
          'hidden bg-muted/50 px-3 py-2 text-xs font-medium text-muted-foreground sm:grid',
        )}
      >
        <span>Blank</span>
        <span className="text-right">Qty</span>
        {showReceived && <span className="text-right">Received</span>}
        <span className="text-right">Unit</span>
        <span className="text-right">Line total</span>
      </div>
      <ul>
        {po.lines.map((l) => (
          <li
            key={l.ssSku}
            className={cn(
              grid,
              'flex flex-wrap items-center gap-x-3 gap-y-0.5 border-t px-3 py-2 text-sm',
            )}
          >
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium">{blankLabel(l)}</span>
              <span className="block truncate text-xs text-muted-foreground">
                {variantLabel(l) || '—'} · <span className="font-mono">{l.ssSku}</span>
              </span>
            </span>
            <span className="text-right font-medium tabular-nums">{l.orderQty}</span>
            {showReceived && (
              <span className="text-right tabular-nums text-muted-foreground">{l.receivedQty}</span>
            )}
            <span className="hidden text-right tabular-nums text-muted-foreground sm:block">
              {money(l.unitCost)}
            </span>
            {/* Shown on a phone too: qty is what you receive against, but the line total is what you
                reconcile the S&S invoice against, and there is width for it. */}
            <span className="text-right tabular-nums">{money(l.lineTotal)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
